import { type InstallableApp } from "~/pwa/installableApps";
import isShortcutLaunch from "~/pwa/isShortcutLaunch";
import isStandaloneDisplay from "~/pwa/isStandaloneDisplay";
import readAuthReturnTo from "~/pwa/readAuthReturnTo";
import resolveStandaloneApp from "~/pwa/resolveStandaloneApp";
import sessionStore from "~/pwa/sessionStore";

const STORAGE_KEY = "ws.launchedApp";
const ROOT_SESSION = "root";

interface StandaloneLaunch {
	app: InstallableApp | null;
	isRootSession: boolean;
}

const plainLaunch: StandaloneLaunch = { app: null, isRootSession: false };
const rootLaunch: StandaloneLaunch = { app: null, isRootSession: true };

let launch: StandaloneLaunch | null = null;

const writeStoredLaunch = (value: string): void => sessionStore.write(STORAGE_KEY, value);

const readStoredLaunch = (): string | null => sessionStore.read(STORAGE_KEY);

const AUTH_CALLBACK_PATH = "/oauth-redirect";

const detectAuthReturnLaunch = (): StandaloneLaunch => {
	const returnTo = readAuthReturnTo(window.location.search);
	if (returnTo === null) return plainLaunch;

	const [returnPath, returnSearch = ""] = returnTo.split("?");
	if (returnPath === "/" || isShortcutLaunch(returnSearch)) {
		writeStoredLaunch(ROOT_SESSION);
		return rootLaunch;
	}

	const { app } = resolveStandaloneApp(true, null, returnPath);
	if (!app) {
		writeStoredLaunch(ROOT_SESSION);
		return rootLaunch;
	}
	writeStoredLaunch(app.key);
	return { app, isRootSession: false };
};

const detectLaunch = (): StandaloneLaunch => {
	if (!isStandaloneDisplay()) return plainLaunch;

	if (isShortcutLaunch()) {
		writeStoredLaunch(ROOT_SESSION);
		return rootLaunch;
	}

	const stored = readStoredLaunch();
	if (stored === ROOT_SESSION) return rootLaunch;
	if (stored) return { app: resolveStandaloneApp(true, stored, "").app, isRootSession: false };

	if (window.location.pathname === AUTH_CALLBACK_PATH) return detectAuthReturnLaunch();

	if (window.location.pathname === "/") {
		writeStoredLaunch(ROOT_SESSION);
		return rootLaunch;
	}

	const { app } = resolveStandaloneApp(true, null, window.location.pathname);
	if (app) {
		writeStoredLaunch(app.key);
		return { app, isRootSession: false };
	}

	// Module manifests are scoped to their own start URL, so only the root install can open on a non-module path.
	writeStoredLaunch(ROOT_SESSION);
	return rootLaunch;
};

const standaloneLaunch = {
	release: (): void => {
		launch = rootLaunch;
		writeStoredLaunch(ROOT_SESSION);
	},
	resolve: (): StandaloneLaunch => {
		launch ??= detectLaunch();
		return launch;
	}
};

export type { StandaloneLaunch };
export default standaloneLaunch;
